import { isPlatformBrowser, NgClass, NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ButtonComponent } from '@examdojo/core/button';
import { CopyToClipboardDirective } from '@examdojo/core/copy-to-clipboard';
import { IconComponent } from '@examdojo/core/icon';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import {
  FieldDefinition,
  getOperation,
  getProcedureCategoryColor,
  Procedure,
  PROCEDURE_CATEGORIES,
  ProcedureCategory,
  Representation,
} from '@examdojo/core/ontology';
import { SingleSelectComponent } from '@examdojo/core/select';
import { NumberInputComponent, TextInputComponent } from '@examdojo/ui/input';
import { MarkdownModule } from 'ngx-markdown';
import { EditorComponent, NGX_MONACO_EDITOR_CONFIG, NgxEditorModel, NgxMonacoEditorConfig } from 'ngx-monaco-editor-v2';
import { FieldTypeService, TypeBadgeComponent } from '../../shared';

// NOTE: AST analysis disabled - TypeScript compiler API can't run in browser
// TODO: Move to backend API endpoint for production use
const analyzeAndSummarize = (_implementation: string) => ({
  success: false,
  error: 'AST analysis requires a backend API (TypeScript compiler cannot run in browser)',
  dependencies: [] as Array<{ operationId: string; name: string; symbol: string; category: string; count: number }>,
  inputFields: [] as string[],
  externalFunctions: [] as string[],
  externalConstants: [] as string[],
});

interface DependencyInfo {
  operationId: string;
  name: string;
  symbol: string;
  category: string;
  count: number;
  description?: string;
  example?: string;
  latex?: string;
  arity?: number;
  categoryClasses: Record<string, boolean>;
  countBadgeClasses: Record<string, boolean>;
  arityLabel: string;
}

interface InputRepresentationData {
  id: string;
  name: string;
  svgPath: string;
  representation: Representation | undefined;
  fields: Array<{ key: string; def: FieldDefinition; isUsed: boolean }>;
}

interface OutputRepresentationData {
  id: string;
  name: string;
  svgPath: string;
  representation: Representation | undefined;
  fields: Array<{ key: string; def: FieldDefinition }>;
}

const DEPENDENCY_CATEGORY_CLASSES: Record<string, Record<string, boolean>> = {
  arithmetic: { 'bg-sky-100': true, 'text-sky-700': true },
  logic: { 'bg-amber-100': true, 'text-amber-700': true },
  function: { 'bg-purple-100': true, 'text-purple-700': true },
  collection: { 'bg-emerald-100': true, 'text-emerald-700': true },
  default: { 'bg-gray-100': true, 'text-gray-700': true },
};

const DEPENDENCY_COUNT_BADGE_CLASSES: Record<string, Record<string, boolean>> = {
  arithmetic: { 'bg-sky-200': true, 'text-sky-800': true },
  logic: { 'bg-amber-200': true, 'text-amber-800': true },
  function: { 'bg-purple-200': true, 'text-purple-800': true },
  collection: { 'bg-emerald-200': true, 'text-emerald-800': true },
  default: { 'bg-gray-200': true, 'text-gray-800': true },
};

@Component({
  selector: 'exam-procedure-details',
  standalone: true,
  imports: [
    EditorComponent,
    MarkdownModule,
    MatMenuModule,
    MatTooltipModule,
    CopyToClipboardDirective,
    IconComponent,
    IconButtonComponent,
    NgClass,
    NgTemplateOutlet,
    TitleCasePipe,
    ReactiveFormsModule,
    NumberInputComponent,
    TextInputComponent,
    SingleSelectComponent,
    ButtonComponent,
    TypeBadgeComponent,
  ],
  templateUrl: './procedure-details.component.html',
  styleUrls: ['./procedure-details.component.scss'],
  providers: [
    {
      provide: NGX_MONACO_EDITOR_CONFIG,
      useValue: {
        baseUrl: 'assets',
      } satisfies NgxMonacoEditorConfig,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProcedureDetailsComponent {
  readonly fieldTypeService = inject(FieldTypeService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly platformId = inject(PLATFORM_ID);

  readonly isBrowser = isPlatformBrowser(this.platformId);
  readonly katexOptions = this.fieldTypeService.katexOptions;
  readonly procedureCategories = PROCEDURE_CATEGORIES;

  readonly editorOptions = {
    automaticLayout: true,
    theme: 'vs',
    language: 'typescript',
    readOnly: true,
    minimap: { enabled: false },
    lineHeight: 20,
    fontSize: 14,
    scrollBeyondLastLine: false,
  };

  readonly data = input.required<Procedure & { procedureCategory?: ProcedureCategory }>();
  readonly representationsMap = input<Map<string, Representation>>(new Map());

  readonly navigateToNode = output<string>();

  readonly editorModel = computed<NgxEditorModel>(() => ({
    value: this.data().implementation ?? '// No implementation provided',
    language: 'typescript',
  }));

  readonly procedureCategory = computed(() => this.data().procedureCategory ?? this.data().category);

  readonly categoryColor = computed(() => {
    const category = this.procedureCategory();
    if (!category) {
      return '#f3f4f6';
    }
    return getProcedureCategoryColor(category);
  });

  readonly inputRepresentations = computed<InputRepresentationData[]>(() => {
    const procedure = this.data();
    const repMap = this.representationsMap();

    return procedure.input_ids.map((id) => {
      const repr = repMap.get(id);
      const fields = this.getModelFieldsForRepresentation(repr);

      return {
        id,
        name: repr?.name ?? id,
        svgPath: this.fieldTypeService.getCategorySvgPath(repr?.category),
        representation: repr,
        fields: fields.map((f) => ({
          ...f,
          isUsed: this.checkFieldUsed(f.key),
        })),
      };
    });
  });

  readonly hasSingleInputRepresentation = computed(() => this.inputRepresentations().length === 1);
  readonly singleInputRepresentation = computed(() => this.inputRepresentations()[0]);

  readonly outputRepresentation = computed<OutputRepresentationData>(() => {
    const procedure = this.data();
    const repMap = this.representationsMap();
    const id = procedure.output_id;
    const repr = repMap.get(id);

    return {
      id,
      name: repr?.name ?? id,
      svgPath: this.fieldTypeService.getCategorySvgPath(repr?.category),
      representation: repr,
      fields: this.getModelFieldsForRepresentation(repr),
    };
  });

  readonly exampleForms = signal<FormGroup[]>([]);
  readonly exampleOutput = signal<Record<string, unknown> | null>(null);
  readonly exampleError = signal<string | null>(null);

  readonly dependencies = signal<DependencyInfo[] | null>(null);
  readonly dependencyInputFields = signal<string[]>([]);
  readonly externalFunctions = signal<string[]>([]);
  readonly externalConstants = signal<string[]>([]);
  readonly dependencyError = signal<string | null>(null);
  readonly isAnalyzingDependencies = signal(false);

  readonly renderedOutputTemplate = computed(() => {
    const exampleOut = this.exampleOutput();
    if (!exampleOut) {
      return '';
    }
    const outputRep = this.representationsMap().get(this.data().output_id);
    if (!outputRep) {
      return '';
    }

    const template = outputRep.template;
    const model = outputRep.model;
    const type = outputRep.type;

    const formatValue = (val: unknown, fieldDef?: unknown): string => {
      if (Array.isArray(val)) {
        const def = fieldDef as Record<string, unknown> | undefined;
        const isLatexArray =
          def?.['type'] === 'array' &&
          typeof def?.['items'] === 'object' &&
          (def['items'] as Record<string, unknown>)?.['format'] === 'latex';
        if (isLatexArray) {
          return val.map((v) => `$${String(v)}$`).join(', ');
        }
        return val.map((v) => String(v)).join(', ');
      }
      return String(val);
    };

    if (!template && model) {
      const firstLatexField = Object.entries(model).find(
        ([, def]) => def.type === 'string' && 'format' in def && def.format === 'latex',
      );
      if (firstLatexField) {
        const [key] = firstLatexField;
        const val = exampleOut[key];
        if (val) {
          return type === 'symbolic' ? `$${String(val)}$` : String(val);
        }
      }

      const firstLatexArrayField = Object.entries(model).find(([, def]) => {
        if (def.type !== 'array') {
          return false;
        }
        const items = (def as unknown as Record<string, unknown>)['items'] as Record<string, unknown> | undefined;
        return items?.['format'] === 'latex';
      });
      if (firstLatexArrayField) {
        const [key] = firstLatexArrayField;
        const val = exampleOut[key];
        if (Array.isArray(val) && val.length > 0) {
          return val.map((v) => `$${String(v)}$`).join(', ');
        }
      }

      if (type === 'numerical' && 'value' in exampleOut) {
        return `$${String(exampleOut['value'])}$`;
      }
      if (type === 'state' && 'state' in exampleOut) {
        const desc = 'description' in exampleOut ? `: ${String(exampleOut['description'])}` : '';
        return `**${String(exampleOut['state'])}**${desc}`;
      }
      return '';
    }

    if (!template) {
      return '';
    }

    const rendered = template.replace(/\{\{(\w+)\}\}/g, (_, key) => {
      const val = exampleOut[key] ?? '';
      const fieldDef = model?.[key];
      return formatValue(val, fieldDef);
    });
    if (type === 'numerical') {
      return `$${rendered}$`;
    }
    return rendered;
  });

  readonly formattedOutputValues = computed(() => {
    const exampleOut = this.exampleOutput();
    if (!exampleOut) {
      return new Map<string, string>();
    }
    const outputRep = this.representationsMap().get(this.data().output_id);
    const formatted = new Map<string, string>();

    for (const key of Object.keys(exampleOut)) {
      const value = exampleOut[key];
      if (value === null || value === undefined) {
        formatted.set(key, '-');
        continue;
      }

      const fieldDef = outputRep?.model?.[key];
      if (fieldDef?.type === 'string' && 'format' in fieldDef && fieldDef.format === 'latex') {
        formatted.set(key, `$${String(value)}$`);
        continue;
      }

      if (fieldDef?.type === 'array' && Array.isArray(value)) {
        const items = (fieldDef as unknown as Record<string, unknown>)['items'] as Record<string, unknown> | undefined;
        const isLatexArray = items?.['format'] === 'latex';
        if (isLatexArray) {
          formatted.set(key, value.map((v) => `$${String(v)}$`).join(', '));
        } else {
          formatted.set(key, value.map((v) => String(v)).join(', '));
        }
        continue;
      }

      if (typeof value === 'number') {
        if (Number.isInteger(value)) {
          formatted.set(key, String(value));
        } else {
          formatted.set(key, String(Math.round(value * 1000000) / 1000000));
        }
        continue;
      }

      formatted.set(key, String(value));
    }

    return formatted;
  });

  readonly exampleFormControls = computed(() => {
    const forms = this.exampleForms();
    const controls = new Map<string, Map<string, FormControl>>();

    forms.forEach((form, repIndex) => {
      const repControls = new Map<string, FormControl>();
      const keys = Object.keys(form.controls);
      for (const key of keys) {
        repControls.set(key, form.get(key) as FormControl);
      }
      controls.set(String(repIndex), repControls);
    });

    return controls;
  });

  readonly enumOptionsCache = computed(() => {
    const inputReps = this.inputRepresentations();
    const cache = new Map<string, Array<{ label: string; value: string }>>();

    for (const rep of inputReps) {
      for (const field of rep.fields) {
        if (field.def.type === 'enum' && field.def.options) {
          const key = `${rep.id}_${field.key}`;
          cache.set(
            key,
            field.def.options.map((opt) => ({ label: String(opt), value: String(opt) })),
          );
        }
      }
    }

    return cache;
  });

  readonly usedFieldNames = computed<Set<string> | null>(() => {
    const implementation = this.data().implementation;
    if (!implementation) {
      return null;
    }
    const result = analyzeAndSummarize(implementation);
    if (!result.success) {
      return null;
    }
    const fieldNames = new Set<string>();
    for (const field of result.inputFields) {
      const dotIndex = field.lastIndexOf('.');
      if (dotIndex !== -1) {
        fieldNames.add(field.substring(dotIndex + 1));
      }
    }
    return fieldNames;
  });

  private readonly dataChangeEffect = effect(() => {
    this.data();
    this.representationsMap();
    this.resetState();
    this.initializeExampleInputs();
  });

  analyzeDependencies(): void {
    const procedure = this.data();
    if (!procedure?.implementation) {
      this.dependencyError.set('No implementation to analyze');
      return;
    }

    this.isAnalyzingDependencies.set(true);
    this.dependencies.set(null);
    this.dependencyInputFields.set([]);
    this.externalFunctions.set([]);
    this.externalConstants.set([]);
    this.dependencyError.set(null);

    try {
      const result = analyzeAndSummarize(procedure.implementation);

      if (!result.success) {
        this.dependencyError.set(result.error ?? 'Analysis failed');
      } else {
        const deps = result.dependencies.map((dep) => {
          const op = getOperation(dep.operationId);
          const arity = op?.arity;
          return {
            ...dep,
            description: op?.description,
            example: op?.example,
            latex: op?.latex,
            arity,
            categoryClasses: this.computeCategoryClasses(dep.category),
            countBadgeClasses: this.computeCountBadgeClasses(dep.category),
            arityLabel: this.computeArityLabel(arity),
          };
        });
        this.dependencies.set(deps);
        this.dependencyInputFields.set(result.inputFields);
        this.externalFunctions.set(result.externalFunctions);
        this.externalConstants.set(result.externalConstants);
      }
    } catch (error) {
      this.dependencyError.set(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      this.isAnalyzingDependencies.set(false);
      this.cdr.markForCheck();
    }
  }

  initializeExampleInputs(): void {
    const procedure = this.data();
    const repMap = this.representationsMap();

    if (!procedure?.input_ids) {
      return;
    }

    const forms = procedure.input_ids.map((id) => {
      const repr = repMap.get(id);
      return this.buildFormForRepresentation(repr);
    });
    this.exampleForms.set(forms);
  }

  getExampleFormControl(repIndex: number, fieldKey: string): FormControl {
    const controls = this.exampleFormControls().get(String(repIndex));
    return controls?.get(fieldKey) ?? new FormControl('');
  }

  getEnumOptions(repId: string, fieldKey: string): Array<{ label: string; value: string }> {
    return this.enumOptionsCache().get(`${repId}_${fieldKey}`) ?? [];
  }

  hasUsedFields(inputRep: InputRepresentationData): boolean {
    return inputRep.fields.some((field) => field.isUsed);
  }

  getArrayFormArray(repIndex: number, fieldKey: string): FormArray {
    const form = this.exampleForms()[repIndex];
    return form?.get(fieldKey) as FormArray;
  }

  getArrayItemControl(repIndex: number, fieldKey: string, itemIndex: number): FormControl {
    const formArray = this.getArrayFormArray(repIndex, fieldKey);
    return formArray?.at(itemIndex) as FormControl;
  }

  addArrayItem(repIndex: number, fieldKey: string, fieldDef: FieldDefinition): void {
    const formArray = this.getArrayFormArray(repIndex, fieldKey);
    if (formArray) {
      const validators = this.getArrayItemValidators(fieldDef);
      formArray.push(new FormControl('', validators));
      this.cdr.markForCheck();
    }
  }

  removeArrayItem(repIndex: number, fieldKey: string, itemIndex: number): void {
    const formArray = this.getArrayFormArray(repIndex, fieldKey);
    if (formArray && formArray.length > 1) {
      formArray.removeAt(itemIndex);
      this.cdr.markForCheck();
    }
  }

  isLatexArrayField(fieldDef: FieldDefinition): boolean {
    return this.fieldTypeService.isLatexArrayField(fieldDef);
  }

  isNumberArrayField(fieldDef: FieldDefinition): boolean {
    if (fieldDef.type !== 'array') {
      return false;
    }
    const items = (fieldDef as unknown as Record<string, unknown>)['items'] as Record<string, unknown> | undefined;
    return items?.['type'] === 'number';
  }

  executeExample(): void {
    this.exampleError.set(null);
    this.exampleOutput.set(null);

    const allValid = this.exampleForms().every((form) => form.valid);
    if (!allValid) {
      this.exampleError.set('Please fix validation errors before executing');
      this.cdr.detectChanges();
      return;
    }

    try {
      const procedure = this.data();
      if (!procedure.execute) {
        this.exampleError.set('No execute function available');
        return;
      }

      const inputValues = this.exampleForms().map((form) => form.value);
      const result = procedure.execute(...inputValues);
      this.exampleOutput.set(result as Record<string, unknown>);
      this.cdr.detectChanges();
    } catch (error) {
      this.exampleError.set(error instanceof Error ? error.message : 'Execution failed');
      this.cdr.detectChanges();
    }
  }

  private computeCategoryClasses(category: string): Record<string, boolean> {
    return DEPENDENCY_CATEGORY_CLASSES[category] ?? DEPENDENCY_CATEGORY_CLASSES['default'];
  }

  private computeCountBadgeClasses(category: string): Record<string, boolean> {
    return DEPENDENCY_COUNT_BADGE_CLASSES[category] ?? DEPENDENCY_COUNT_BADGE_CLASSES['default'];
  }

  private computeArityLabel(arity: number | undefined): string {
    switch (arity) {
      case 1:
        return 'Unary (1 argument)';
      case 2:
        return 'Binary (2 arguments)';
      case 3:
        return 'Ternary (3 arguments)';
      default:
        return arity ? `${arity} arguments` : 'Unknown';
    }
  }

  private resetState(): void {
    this.exampleForms.set([]);
    this.exampleOutput.set(null);
    this.exampleError.set(null);
    this.dependencies.set(null);
    this.dependencyInputFields.set([]);
    this.externalFunctions.set([]);
    this.externalConstants.set([]);
    this.dependencyError.set(null);
  }

  private getModelFieldsForRepresentation(
    repr: Representation | undefined,
  ): Array<{ key: string; def: FieldDefinition }> {
    if (!repr?.model) {
      return [];
    }
    return Object.entries(repr.model)
      .map(([key, def]) => ({ key, def }))
      .sort((a, b) => {
        if (!a.def.optional && b.def.optional) {
          return -1;
        }
        if (a.def.optional && !b.def.optional) {
          return 1;
        }
        return 0;
      });
  }

  private checkFieldUsed(fieldKey: string): boolean {
    const usedFields = this.usedFieldNames();
    if (usedFields === null) {
      return true;
    }
    return usedFields.has(fieldKey);
  }

  private buildFormForRepresentation(repr: Representation | undefined): FormGroup {
    const group: Record<string, FormControl | FormArray> = {};

    if (!repr?.model) {
      return new FormGroup(group);
    }

    for (const [key, field] of Object.entries(repr.model)) {
      if (field.type === 'constant') {
        continue;
      }

      if (field.type === 'array') {
        const exampleValue = repr.example_data?.[key] ?? field.example;
        const initialValues = Array.isArray(exampleValue) ? exampleValue : [''];
        const itemValidators = this.getArrayItemValidators(field);

        const controls = initialValues.map((val) => new FormControl(val, itemValidators));
        group[key] = new FormArray(controls.length > 0 ? controls : [new FormControl('', itemValidators)]);
        continue;
      }

      const validators: ValidatorFn[] = [Validators.required];
      const value = repr.example_data?.[key] ?? field.example ?? null;

      if (field.type === 'number') {
        if (field.min !== undefined) {
          if (field.min_exclusive) {
            validators.push((control) => {
              const val = control.value;
              if (val !== null && val !== undefined && val <= field.min!) {
                return { minExclusive: { min: field.min, actual: val } };
              }
              return null;
            });
          } else {
            validators.push(Validators.min(field.min));
          }
        }
        if (field.max !== undefined) {
          if (field.max_exclusive) {
            validators.push((control) => {
              const val = control.value;
              if (val !== null && val !== undefined && val >= field.max!) {
                return { maxExclusive: { max: field.max, actual: val } };
              }
              return null;
            });
          } else {
            validators.push(Validators.max(field.max));
          }
        }

        if (field.domain === 'natural') {
          validators.push(Validators.pattern(/^[0-9]+$/));
          if (field.min === undefined) {
            validators.push(Validators.min(1));
          }
        } else if (field.domain === 'integer') {
          validators.push(Validators.pattern(/^-?[0-9]+$/));
        }
      }

      group[key] = new FormControl(value, validators);
    }

    return new FormGroup(group);
  }

  private getArrayItemValidators(fieldDef: FieldDefinition): ValidatorFn[] {
    const validators: ValidatorFn[] = [Validators.required];
    const items = (fieldDef as unknown as Record<string, unknown>)['items'] as Record<string, unknown> | undefined;

    if (!items) {
      return validators;
    }

    if (items['format'] === 'latex') {
      validators.push(this.latexValidator());
    } else if (items['type'] === 'number') {
      validators.push(Validators.pattern(/^-?\d*\.?\d+$/));
    }

    return validators;
  }

  private latexValidator(): ValidatorFn {
    return (control) => {
      const value = control.value;
      if (!value) {
        return null;
      }

      const invalidChars = /[<>]/;
      if (invalidChars.test(value)) {
        return { latex: 'Invalid characters in LaTeX' };
      }

      let braceCount = 0;
      for (const char of value) {
        if (char === '{') {
          braceCount++;
        }
        if (char === '}') {
          braceCount--;
        }
        if (braceCount < 0) {
          return { latex: 'Unmatched closing brace' };
        }
      }
      if (braceCount !== 0) {
        return { latex: 'Unmatched opening brace' };
      }

      return null;
    };
  }
}
